import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICreativeMessage, CreativeMessageRole } from '@packages/global';
import MarkdownRenderer from '../common/MarkdownRenderer';
import { useToast } from '../../stores/toastStore';
import { SparklesIcon, UserCircleIcon, ClipboardIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';

interface MessageBubbleProps {
  message: ICreativeMessage;
  onDelete: (messageId: string) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onDelete }) => {
  const { t } = useTranslation('creative');
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const isUser = message.role === CreativeMessageRole.USER;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      toast.success(t('message.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('message.copyFailed'));
    }
  };

  const handleDelete = () => {
    onDelete(message.id);
    toast.success(t('message.deleted'));
  };

  const actions = (
    <div className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isUser ? 'justify-end' : ''}`}>
      <button
        onClick={handleCopy}
        className="p-0.5 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
        title={t('message.copy')}
      >
        {copied ? <CheckIcon className="w-3.5 h-3.5 text-success" /> : <ClipboardIcon className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={handleDelete}
        className="p-0.5 text-gray-500 hover:text-error transition-colors cursor-pointer"
        title={t('message.delete')}
      >
        <TrashIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  if (isUser) {
    return (
      <div className="group flex items-start justify-end gap-3">
        <div className="flex flex-col items-end gap-1 max-w-[70%]">
          <div className="bg-primary rounded-2xl rounded-tr-sm px-4 py-3">
            <p className="text-white whitespace-pre-wrap break-words text-sm">{message.content}</p>
          </div>
          {actions}
        </div>
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-bg-elevated flex items-center justify-center">
          <UserCircleIcon className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
        <SparklesIcon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex flex-col gap-1 max-w-[70%]">
        <div className="bg-bg-surface rounded-2xl rounded-tl-sm px-4 py-3">
          <MarkdownRenderer content={message.content} />
        </div>
        {actions}
      </div>
    </div>
  );
};

export default MessageBubble;
